import { Bar, Bubble } from 'react-chartjs-2';

import ChartSection from './ChartSection';
import {
  totalValuePlugin,
  barStackedChartOptions
} from 'utils/chart-options/bar-stacked-chart';
import { Dataset, HasData } from 'utils/chart-options/chart-collection';
import { imagePlugin } from 'utils/chart-plugins/image-plugin';
import { bubbleChartOptions } from 'utils/chart-options/bubble-charts';
import { getCustomOptions } from 'utils/chart-options/bar-chart-options';
import {
  ChartsARRRangeLabels,
  ChartsAvgDealSizeLabels
} from 'utils/helpers/chartsDataHelpers';

interface GetCharts {
  chartKey: number;
  xAxisSwitch: boolean;
  generatingPDF: boolean;
  companyLogoURL: string;
  bubbleChartData: { title: string; datasets: any[] }[];
  hasData: HasData;
  barChartData: { labels: string[]; datasets: Dataset[]; title: string }[];
}
const chartsClassName = 'max-w-full ease-out duration-300 div2PDF';

export const getCharts = ({
  hasData,
  chartKey,
  xAxisSwitch,
  barChartData,
  generatingPDF,
  companyLogoURL,
  bubbleChartData
}: GetCharts) => [
  {
    title: 'Growth and Retention',
    charts: [
      {
        id: 0,
        title: barChartData[0]?.title,
        color: barChartData[0]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'We believe one of the most important metrics to track is ARR growth, which measures the change in ARR compared to the previous year. The drivers of ARR growth are also important to understand - both in terms of size and quality of customers, as well as variation by sales motion.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[0].datasets[0]}
            datasets={barChartData[0].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[0]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 1,
        title: barChartData[10]?.title,
        color: barChartData[10]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'New logos are the primary driver of ARR growth until software companies reach ~$200M ARR, when upsell and cross-sell motions scale and customer expansion begins to make up more than 50% of gross new ARR.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[10].datasets[0]}
            datasets={barChartData[10].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[10]}
                options={barStackedChartOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  false
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 2,
        title: barChartData[11]?.title,
        color: barChartData[11]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'We observe that new logo growth in the earlier stages is a strong measure of product market fit and GTM scalability.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[11].datasets[0]}
            datasets={barChartData[11].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[11]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 3,
        title: barChartData[8]?.title,
        color: barChartData[8]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'The software quick ratio measures the growth of recurring revenue over a certain period in comparison to the contraction. Not all growth is equal, so it is important to maintain a higher quick ratio which implies that your growth is healthy and efficient. Generally, the "optimum" software quick ratio is around 4x.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[8].datasets[0]}
            datasets={barChartData[8].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[8]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  false
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 4,
        title: barChartData[1]?.title,
        color: barChartData[1]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'Gross dollar retention is a key indicator of customer stability for software companies and reflects the portion of recurring revenue retained from existing customers, excluding the impact of expansion. Unlike net dollar retention, gross dollar retention focuses solely on revenue lost from downgrades and churn, making it a pure measure of product stickiness and customer loyalty. High gross retention signals that customers continue to find value in the product, while lower rates highlight risks in adoption or satisfaction. Compass software benchmarks reflect annualized quarterly gross dollar retention.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[1].datasets[0]}
            datasets={barChartData[1].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[1]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 5,
        title: barChartData[14]?.title,
        color: barChartData[14]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'Net retention is arguably the most important gauge of business health for software companies and the efficiency of their revenue generation. Net dollar retention specifically accounts for expansion, downsell, and churn which renders it a robust measure of both product strength and customer success motions. There are a number of different ways to calculate net retention. Compass software benchmarks reflect annualized quarterly net dollar retention.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[14].datasets[0]}
            datasets={barChartData[14].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[14]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      }
    ]
  },
  {
    title: 'Operational Efficiency',
    charts: [
      {
        id: 6,
        title: barChartData[2]?.title,
        color: barChartData[2]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'The “magic” of this metric lies in its ability to measure revenue generation against sales & marketing spend while accounting for the lag of a typical sales cycle in order to understand the efficiency of sales and marketing spend and teams at a more nuanced level.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[2].datasets[0]}
            datasets={barChartData[2].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[2]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  false
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 7,
        title: bubbleChartData[0]?.title,
        color: bubbleChartData[0]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'The Rule of 40 is a rule of thumb that measures the tradeoff between growth and profitability; a high-performing software company should generally meet or exceed 40%. Typically real weight begins to be placed against this metric for companies with at least ~$25M in topline revenue.',
        component: (
          <ChartSection
            isRuleof40
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={bubbleChartData[0].datasets[0]}
            datasets={bubbleChartData[0].datasets}
            chart={
              <Bubble
                key={chartKey}
                data={bubbleChartData[0]}
                options={bubbleChartOptions(true, generatingPDF)}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 8,
        title: barChartData[12]?.title,
        color: barChartData[12]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'For any business, you want to make sure you are earning more customers than the amount paid to acquire them. CAC payback period calculates the number of months needed to pay back any customer acquisition costs - effectively showing your break-even point.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={
              xAxisSwitch ? ChartsARRRangeLabels : ChartsAvgDealSizeLabels
            }
            data={barChartData[12].datasets[0]}
            datasets={barChartData[12].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[12]}
                options={getCustomOptions(
                  xAxisSwitch,
                  companyLogoURL,
                  generatingPDF,
                  false,
                  false,
                  true,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 9,
        title: barChartData[4]?.title,
        color: barChartData[4]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'Burn multiple is one of many ways to measure capital efficiency - a metric that becomes increasingly important in the current environment. A burn multiple under 1.5x is generally a great goal for companies to strive toward that are experiencing strong growth.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[4].datasets[0]}
            datasets={barChartData[4].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[4]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  false
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 10,
        title: barChartData[6]?.title,
        color: barChartData[6]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'Over time as software companies scale, revenue should generally start to outpace total operating spend once companies reach ~$200M+ in ARR. Sales and marketing typically comprises the largest proportion of total spend as companies invest in the GTM engine to fuel growth.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[6].datasets[0]}
            datasets={barChartData[6].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[6]}
                options={barStackedChartOptions(
                  true,
                  companyLogoURL,
                  generatingPDF
                )}
                plugins={[totalValuePlugin, imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      }
    ]
  },
  {
    title: 'Profitability and Cash Flow',
    charts: [
      {
        id: 11,
        title: barChartData[3]?.title,
        color: barChartData[3]?.datasets[0]?.backgroundColor,
        tooltipContent:
          "Gross margin we believe is vital for software companies as it indicates profitability and scalability. software companies typically have ~70%+ gross margins and are able to reduce COGS on a per unit basis as they scale. It is important to note a company's growth motion and sector should be considered when benchmarking gross margin.",
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[3].datasets[0]}
            datasets={barChartData[3].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[3]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 12,
        title: barChartData[7]?.title,
        color: barChartData[7]?.datasets[0]?.backgroundColor,
        tooltipContent:
          "FCF margin indicates a company's ability to generate cash from its operations after accounting for capital expenditures. This reflects the true liquidity position and financial health, ensuring the company has sufficient cash to invest in growth, pay down debt, and/or return value to shareholders. In today's macro environment, achieving profitability or having a clear path to profitability is usually a prerequisite to going public.",
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[7].datasets[0]}
            datasets={barChartData[7].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[7]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      }
    ]
  },
  {
    title: 'Headcount',
    charts: [
      {
        id: 13,
        title: barChartData[9]?.title,
        color: barChartData[9]?.datasets[0]?.backgroundColor,
        tooltipContent:
          "Sales and marketing typically constitutes the largest proportion of an organization's total headcount, followed by R&D. Typically R&D starts to comprise a smaller proportion of total headcount as companies invest in go-to-market excellence and operational oversight leading up to IPO.",
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[9].datasets[0]}
            datasets={barChartData[9].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[9]}
                options={barStackedChartOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  false
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 14,
        title: barChartData[5]?.title,
        color: barChartData[5]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'ARR per FTE is another efficiency metric that measures how much ARR is generated per full-time employee. It serves as an effective gauge of whether a company is burning too much relative to employee productivity or if there is an opportunity to invest further.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[5].datasets[0]}
            datasets={barChartData[5].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[5]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  false,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      },
      {
        id: 15,
        title: barChartData[13]?.title,
        color: barChartData[13]?.datasets[0]?.backgroundColor,
        tooltipContent:
          'Over time, ARR per FTE should outpace OpEx per FTE as companies find increasing leverage and improve employee productivity. FTE productivity tends to surpass FTE efficiency around the ~$150M ARR scale.',
        component: (
          <ChartSection
            hasData={hasData}
            labels={ChartsARRRangeLabels}
            data={barChartData[13].datasets[0]}
            datasets={barChartData[13].datasets}
            chart={
              <Bar
                key={chartKey}
                data={barChartData[13]}
                options={getCustomOptions(
                  true,
                  companyLogoURL,
                  generatingPDF,
                  false,
                  true
                )}
                plugins={[imagePlugin]}
                className={chartsClassName}
              />
            }
          />
        )
      }
    ]
  }
];
